import {
    FormLayout,
    Form,
    TextField,
    Button,
    Select,
    LegacyStack,
    Frame,
    Checkbox,
    Text,
    Modal,
    LegacyCard,
    ResourceList,
    ResourceItem,
} from "@shopify/polaris";
import { useState, useCallback, useEffect } from 'react';
import Api from '../../helpers/api';
import {cloneDeep} from 'lodash';
import {CountrySelector, StateSelector} from "../Selectors";
import {filterShippingMethods, getFormError} from "../../helpers/utils";
import {useToast} from "@shopify/app-bridge-react";

const CustomerForm = props => {

    const [loading, setLoading] = useState(false);
    const [allShippingMethods, setAllShippingMethods] = useState({});
    const [shippingMethods, setShippingMethods] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState([]);
    const toast = useToast();

    useEffect(() => {
        Api.getShippingMethods().then(results => {
            setAllShippingMethods(results.data);
        });
        Api.getPaymentTerms().then(results => {
            setPaymentTerms(results.data.data);
        });
    }, [])

    const emptyCustomer = () => {
        return {
            'status': 'approved',  // Internal customers are just approved.
            'first_name': '',
            'last_name': '',
            'email': '',
            'shipping_method': null,
            'credit_limit': '',
            'discount': '',
            'phone': '',
            'notes': '',
            'is_on_hold': false,
            'account_number': '',
            'shopify_company_location_id': '',
            credit_enabled: false,
            credit_balance: 0,

            assign_billing_address: false,
            billing_address_id: null,
            billing_address: null,

            location: {
                company: '',
                address1: '',
                address2: '',
                city: '',
                zip_code: '',
                country: '',
                state: '',
                credit_enabled: false,
                credit_balance: 0,
            },

            payment_terms_template_id: null,

            sync_hash: null,
        }
    };

    const existingCustomer = () => {
        const base = {
            ...props.customer,
            ...props.customer.additional_data,
            shipping_method: props.customer?.shipping_method ? {
                name: props.customer.shipping_method?.name ?? '',
                price: props.customer.shipping_method?.price ?? '0',
                key: props.customer.shipping_method?.shopify_rate_id ?? props.customer.shipping_method?.key
            } : null,
            id: props.customer.id,
            status: props.customer.status,
            credit_enabled: props.customer.credit_enabled ?? false,
            credit_balance: props.customer.credit_balance ?? 0,
            sync_hash: props.customer.sync_hash,
            payment_terms_template_id: props.customer.payment_terms_template_id
        };

        return {
            ...base,
            assign_billing_address: !!props.customer?.billing_address,
            billing_address_id: props.customer?.billing_address?.id ?? null,
            billing_address: props.customer?.billing_address ?? null,
        };
    }

    const [toastActive, setToastActive] = useState(false);
    const [form, setForm] = useState((props.customer && props.updating)
        ? existingCustomer()
        : emptyCustomer() );

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const address = props.updating
            ? form.default_location?.shipping
            : form.location;

        if (allShippingMethods && (allShippingMethods.data || allShippingMethods.custom))
            setShippingMethods(filterShippingMethods(address, allShippingMethods));
    }, [allShippingMethods, form]);

    const handleInputChange = useCallback((value, id) => {
        const payload = {
            [id]: value,
        };

        console.log(payload);

        setForm({
            ...form,
            ...payload,
        });
        console.log(form);
    }, [form]);

    const handleAddressChange = useCallback((value, id) => {
        let keys = id.split('.');
        let clonedForm = cloneDeep(form);
        clonedForm[keys[0]][keys[1]] = value;
        setForm(clonedForm);
    }, [form]);

    const handleShippingMethodChange = useCallback((key) => {
        let clonedForm = cloneDeep(form);

        if (!key || key.length == 0) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        let method = null;
        shippingMethods.forEach((item) => {
            if (item.key == key)
                method = item;
        });

        if (!method) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        clonedForm.shipping_method = {
            name: method.name,
            price: method.price,
            key: method.key,
        }
        setForm(clonedForm);
    }, [form, shippingMethods]);

    const changeCustomShippingMethod = useCallback((value, id) => {
        let clonedForm = cloneDeep(form);
        clonedForm.shipping_method[id] = value;
        setForm(clonedForm);
    }, [form]);

    const handleSubmit = () => {
        setLoading(true);
        setErrors({});

        const payload = form.assign_billing_address ? form : {
            ...form,
            billing_address_id: null,
            billing_address: null,
        };

        Api.upsertCustomer(payload)
            .then (({ data }) => {
                props.onCustomerCreated(data.data);
                setLoading(false);
            })
            .catch ( error => {
                setLoading(false);

                if (error.response.status == 422) {
                    setErrors(error.response.data.errors);
                    if (error.response.data.errors.shopify_error) {
                        toast.show(error.response.data.errors.shopify_error[0], {isError: true});
                    } else {
                        toast.show("There has been an error processing the form.", {isError: true});
                        setErrors(error.response.data.errors);
                    }
                } else {
                    toast.show("There has been an error processing the form.", {isError: true});
                }

                setLoading(false);

                if (error.response.status != 422) {
                    throw error;
                }
            });
    }

    const showError = name => getFormError(errors, name);

    const [billingModalOpen, setBillingModalOpen] = useState(false);
    const [billingSearch, setBillingSearch] = useState('');
    const [billingResults, setBillingResults] = useState([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [billingPage, setBillingPage] = useState(1);
    const [billingLastPage, setBillingLastPage] = useState(1);

    const loadBillingAddresses = useCallback(async (searchQuery = '', pageNumber = 1) => {
        setBillingLoading(true);
        try {
            const response = await Api.getBillingAddresses(searchQuery, pageNumber);
            setBillingResults(response.data.data || []);
            setBillingPage(response.data.meta.current_page || 1);
            setBillingLastPage(response.data.meta.last_page || 1);
        } finally {
            setBillingLoading(false);
        }
    }, []);

    const openBillingModal = useCallback(() => {
        setBillingModalOpen(true);
        loadBillingAddresses('', 1);
        setBillingSearch('');
    }, [loadBillingAddresses]);

    const selectBillingAddress = useCallback((item) => {
        setForm(prev => ({
            ...prev,
            assign_billing_address: true,
            billing_address_id: item.id,
            billing_address: item,
        }));
        setBillingModalOpen(false);
    }, []);

    const clearBillingAddress = useCallback(() => {
        setForm(prev => ({
            ...prev,
            billing_address_id: null,
            billing_address: null,
        }));
    }, []);

    const toggleAssignBilling = useCallback((checked) => {
        setForm(prev => {
            const next = { ...prev, assign_billing_address: checked };
            if (!checked) {
                next.billing_address_id = null;
                next.billing_address = null;
            }
            return next;
        });
    }, []);

    return (
        <Frame>
            <Form onSubmit={handleSubmit}>
            <FormLayout>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <TextField value={form.account_number} onChange={handleInputChange} label="Account Number" type="text" id="account_number" error={showError('account_number')} />
                    </LegacyStack>
                </FormLayout.Group>

                <FormLayout.Group>
                    <LegacyStack vertical>
                        <Select label="Default Shipping Method"
                                options={[
                                    { label: '--Select Shipping Method--', value: '' },
                                    ...(shippingMethods.map((method) => {
                                        return {
                                            label: method.label,
                                            value: method.key + ''
                                        }
                                    }))
                                ]}
                                onChange={handleShippingMethodChange}
                                value={form.shipping_method?.key + ''}
                                id="shipping_method"
                                error={showError('shipping_method')}
                        />
                        { form.shipping_method?.key == 'custom' && (
                            <LegacyStack distribution={"fillEvenly"}>
                                <TextField label="Shipping Method Name" id="name" value={form.shipping_method.name} onChange={changeCustomShippingMethod} />
                                <TextField label="Shipping Method Price" id="price" type={"number"} min={0} step={1.0} value={form.shipping_method.price.toString()} onChange={changeCustomShippingMethod} />
                            </LegacyStack>
                        ) }
                    </LegacyStack>
                </FormLayout.Group>

                <FormLayout.Group>
                    <LegacyStack vertical>
                        <Select label="Payment terms" options={[
                            { label: '--Select Payment Terms--', value: '' },
                            ...(paymentTerms.map((term) => {
                                return {
                                    label: term.name,
                                    value: term.id + ''
                                }
                            }))]}
                                value={form.payment_terms_template_id + ''}  onChange={handleInputChange}
                                id="payment_terms_template_id"
                                />
                    </LegacyStack>
                </FormLayout.Group>

                <hr />
                <Text variant="headingSm" as="h3">Billing</Text>
                <FormLayout.Group>
                    <Checkbox
                        checked={!!form.assign_billing_address}
                        onChange={toggleAssignBilling}
                        label="Assign billing address"
                        id="assign_billing_address"
                        helpText="Assign billing address to this customer"
                    />

                    {form.assign_billing_address && form.billing_address && (
                        <LegacyStack vertical spacing="extraTight">
                            <Text as="p" variant="bodyMd">
                                <b>{form.billing_address.business_name}</b><br/>
                                {[
                                  form.billing_address.address_line_1,
                                  form.billing_address.address_line_2,
                                  form.billing_address.city,
                                  form.billing_address.zip_code,
                                  form.billing_address.country
                                ].filter(Boolean).join(', ')}
                            </Text>
                            <LegacyStack>
                                <Button onClick={openBillingModal}>Change</Button>
                                <Button onClick={clearBillingAddress} tone="critical">Remove</Button>
                            </LegacyStack>
                        </LegacyStack>
                    )}

                    {form.assign_billing_address && !form.billing_address && (
                        <Button onClick={openBillingModal} variant="primary">Select billing address</Button>
                    )}
                </FormLayout.Group>

                <Modal
                    open={billingModalOpen}
                    onClose={() => setBillingModalOpen(false)}
                    title="Select billing address"
                    primaryAction={null}
                    secondaryActions={[]}
                    large
                >
                    <Modal.Section>
                        <FormLayout>
                            <TextField
                                label="Search"
                                value={billingSearch}
                                onChange={setBillingSearch}
                                placeholder="Search"
                                autoComplete="off"
                                connectedRight={
                                    <Button loading={billingLoading} onClick={() => loadBillingAddresses(billingSearch, 1)} variant="primary">
                                        Search
                                    </Button>
                                }
                            />
                            <LegacyCard>
                                <ResourceList
                                    loading={billingLoading}
                                    resourceName={{ singular: 'Billing Address', plural: 'Billing Addresses' }}
                                    items={billingResults}
                                    renderItem={(item) => {
                                        return (
                                            <ResourceItem
                                                id={item.id}
                                                onClick={() => selectBillingAddress(item)}
                                                accessibilityLabel={`Select billing address ${item.business_name}`}
                                            >
                                                <h3>
                                                    <Text as="span" fontWeight="semibold">{item.business_name}</Text>
                                                </h3>
                                                <div>{item.contact_email}</div>
                                            </ResourceItem>
                                        );
                                    }}
                                />
                            </LegacyCard>
                            <LegacyStack alignment="center" distribution="equalSpacing">
                                <Button disabled={billingPage <= 1} onClick={() => loadBillingAddresses(billingSearch, billingPage - 1)}>Prev</Button>
                                <Text as="span">Page {billingPage} / {billingLastPage}</Text>
                                <Button disabled={billingPage >= billingLastPage} onClick={() => loadBillingAddresses(billingSearch, billingPage + 1)}>Next</Button>
                            </LegacyStack>
                        </FormLayout>
                    </Modal.Section>
                </Modal>

                <hr />
                <FormLayout.Group>
                    <Checkbox
                        checked={form.credit_enabled}
                        onChange={handleInputChange}
                        label="Credit Enabled"
                        id="credit_enabled"
                        error={showError('credit_enabled')}
                        helpText={form.credit_expires_at ? `Credit expires at ${form.credit_expires_at}` : null}
                    />
                    <TextField
                        value={(form.credit_enabled ? form.credit_balance : 0) + ''}
                        onChange={handleInputChange}
                        disabled={!form.credit_enabled}
                        label="Credit Balance"
                        type="number"
                        id="credit_balance"
                        error={showError('credit_balance')}
                    />
                </FormLayout.Group>
                <FormLayout.Group>
                        <TextField value={form.credit_limit} onChange={handleInputChange} label="Credit Limit" type="text" id="credit_limit" error={showError('credit_limit')} />
                        <TextField value={form.discount} onChange={handleInputChange} label="Discount (%)" type="text" id="discount" error={showError('discount')} />
                </FormLayout.Group>

                <hr />
                <Text variant="headingSm" as="h3">Contact Information</Text>
                <FormLayout.Group>
                    <LegacyStack vertical>
                        <TextField value={form.email} onChange={handleInputChange} label="Email" type="email" id="email" error={showError('email')} />
                        <TextField label="Phone" value={form.phone} onChange={handleInputChange} type="tel" id="phone" error={showError('phone')} />
                    </LegacyStack>
                    <LegacyStack vertical>
                        <TextField label="First Name" value={form.first_name} onChange={handleInputChange} type="text" id="first_name" error={showError('first_name')} />
                        <TextField label="Company Name" value={form.last_name} onChange={handleInputChange} type="text" id="last_name" error={showError('last_name')} />
                    </LegacyStack>
                </FormLayout.Group>

                {props.creating && (
                    <>
                        <hr />
                        <Text variant="headingMd" as="h2">Location</Text>
                        <div style={{marginLeft: '-2rem'}}>
                            <FormLayout.Group>
                                <Checkbox
                                    checked={form.location?.credit_enabled ?? false}
                                    onChange={handleAddressChange}
                                    label="Credit Enabled"
                                    id="location.credit_enabled"
                                    error={showError('location.credit_enabled')}
                                />
                                <TextField
                                    value={((form.location?.credit_enabled ?? false) ? form.location?.credit_balance : 0) + ''}
                                    onChange={handleInputChange}
                                    disabled={!(form.location?.credit_enabled ?? false)}
                                    label="Credit Balance"
                                    type="number"
                                    id="location.credit_balance"
                                    error={showError('location.credit_balance')}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group >
                                <LegacyStack vertical>
                                    <TextField label="Company" value={form.location?.company} onChange={handleAddressChange} type="text" id="location.company" error={showError('location.company')} />
                                    <TextField label="Address 1" value={form.location?.address1} onChange={handleAddressChange} type="text" id="location.address1" error={showError('location.address1')} />
                                    <TextField label="Address 2" value={form.location?.address2} onChange={handleAddressChange} type="text" id="location.address2" error={showError('location.address2')} />
                                    <TextField label="City" value={form.location?.city} onChange={handleAddressChange} type="text" id="location.city" error={showError('location.city')} />
                                    <TextField label="Postal Code" value={form.location?.zip_code} onChange={handleAddressChange} type="text" id="location.zip_code" error={showError('location.zip_code')} />
                                    <CountrySelector onChange={(val) => {handleAddressChange(val, "location.country")}}
                                                     value={form.location?.country} error={showError('location.country')} />
                                    <StateSelector onChange={(val) => {handleAddressChange(val, "location.state")}}
                                                   value={form.location?.state} error={showError('location.state')} selectedCountry={form.location?.country} />
                                </LegacyStack>
                            </FormLayout.Group>
                        </div>
                    </>
                )}

                <hr />
                <FormLayout.Group>
                    <TextField label="Notes" value={form.notes} onChange={handleInputChange} type="text" id="notes" error={showError('notes')} multiline={4} />
                </FormLayout.Group>
                <Button loading={loading} submit  variant="primary">Submit</Button>
            </FormLayout>
            </Form>
        </Frame>
    );
}

export default CustomerForm;
